import React, { useState } from 'react';
import {
  CheckCircle,
  ArrowLeft,
  Search,
  Filter,
  Plus,
  Edit,
  Eye,
  Clock,
  User,
  AlertTriangle,
  XCircle,
  FileText,
  Calendar,
  Activity,
  TrendingUp,
  BarChart3,
  Settings,
  Download,
  MessageSquare,
  ArrowRight,
  Zap
} from 'lucide-react';
import { approvalWorkflowData } from '../../data/purchaseCycleData';

const ApprovalWorkflow = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const { approvalWorkflows, pendingApprovals, approvalDashboard } = approvalWorkflowData;

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'cancelled': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return '已核准';
      case 'pending': return '待核准';
      case 'rejected': return '已拒絕';
      case 'cancelled': return '已取消';
      default: return '未知';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'normal': return 'text-blue-600 bg-blue-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 'urgent': return '緊急';
      case 'high': return '高';
      case 'normal': return '一般';
      case 'low': return '低';
      default: return '未知';
    }
  };

  const getRequestTypeText = (type) => {
    switch (type) {
      case 'purchase_order': return '採購訂單';
      case 'contract': return '合約';
      case 'budget': return '預算';
      case 'payment': return '付款';
      default: return type;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-TW');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('zh-TW');
  };

  const calculateDaysRemaining = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const renderWorkflowBanner = () => {
    if (!navigationSource) return null;
    
    return (
      <div className="mb-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6" />
            <div>
              <h2 className="text-lg font-semibold">採購週期管理 - 核准流程</h2>
              <p className="text-orange-100 text-sm">Purchase Cycle Management - Approval Workflow</p>
            </div>
          </div>
          <button
            onClick={onBack}
            className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回採購週期</span>
          </button>
        </div>
      </div>
    );
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">待核准項目</p>
              <p className="text-2xl font-bold text-gray-900">{approvalDashboard.totalPendingApprovals}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <AlertTriangle className="w-4 h-4 text-orange-500 mr-1" />
            <span className="text-orange-600">高優先 {approvalDashboard.highPriorityApprovals} 項</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">緊急核准</p>
              <p className="text-2xl font-bold text-red-600">{approvalDashboard.urgentApprovals}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <Clock className="w-4 h-4 text-red-500 mr-1" />
            <span className="text-red-600">需要立即處理</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">逾期項目</p>
              <p className="text-2xl font-bold text-red-600">{approvalDashboard.overdueApprovals}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <AlertTriangle className="w-4 h-4 text-red-500 mr-1" />
            <span className="text-red-600">超過截止時間</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">平均處理時間</p>
              <p className="text-2xl font-bold text-gray-900">{approvalDashboard.averageApprovalTime}h</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600">效率提升中</span>
          </div>
        </div>
      </div>

      {/* Approval by Type */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">核准類型分布</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {approvalDashboard.approvalsByType.map((type, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{getRequestTypeText(type.type)}</h4>
                <span className="text-sm text-gray-500">{type.count} 項</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(type.value)}</p>
              <p className="text-sm text-gray-500">總金額</p>
            </div>
          ))}
        </div>
      </div>

      {/* Approval by Level */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">核准層級統計</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {approvalDashboard.approvalsByLevel.map((level, index) => (
            <div key={index} className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold text-blue-600">第 {level.level} 層</div>
              <div className="text-sm text-gray-500 mt-1">待核准 {level.count} 項</div>
              <div className="text-sm text-gray-500">平均時間 {level.avgTime} 小時</div>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly Trend */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">週度趨勢</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {approvalDashboard.weeklyTrend.map((week, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{week.week}</h4>
                <Calendar className="w-4 h-4 text-gray-500" />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">提交</span>
                  <span className="text-sm font-medium text-blue-600">{week.submitted}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">核准</span>
                  <span className="text-sm font-medium text-green-600">{week.approved}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">拒絕</span>
                  <span className="text-sm font-medium text-red-600">{week.rejected}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">快速操作</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setActiveView('pending')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Clock className="w-5 h-5 text-yellow-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">待核准項目</p>
              <p className="text-sm text-gray-500">處理等待核准的申請</p>
            </div>
          </button>
          
          <button
            onClick={() => setActiveView('workflows')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Settings className="w-5 h-5 text-blue-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">流程設定</p>
              <p className="text-sm text-gray-500">管理核准流程規則</p>
            </div>
          </button>
          
          <button
            onClick={() => setActiveView('history')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Activity className="w-5 h-5 text-green-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">核准歷史</p>
              <p className="text-sm text-gray-500">查看歷史核准記錄</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const renderPendingApprovals = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">待核准項目</h2>
          <p className="text-gray-600">處理等待核准的申請</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            <Download className="w-4 h-4" />
            <span>匯出</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="搜尋申請標題..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有類型</option>
            <option value="purchase_order">採購訂單</option>
            <option value="contract">合約</option>
            <option value="budget">預算</option>
            <option value="payment">付款</option>
          </select>
          
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有優先級</option>
            <option value="urgent">緊急</option>
            <option value="high">高</option>
            <option value="normal">一般</option>
            <option value="low">低</option>
          </select>
          
          <button className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors">
            <Filter className="w-4 h-4" />
            <span>進階篩選</span>
          </button>
        </div>
      </div>

      {/* Pending Approvals List */}
      <div className="space-y-4">
        {pendingApprovals.map((approval) => {
          const daysRemaining = calculateDaysRemaining(approval.dueDate);
          const isOverdue = daysRemaining < 0;
          const isUrgent = daysRemaining <= 1;

          return (
            <div key={approval.id} className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{approval.requestTitle}</h3>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(approval.priority)}`}>
                      {getPriorityText(approval.priority)}
                    </span>
                    {isOverdue && (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-600">
                        逾期
                      </span>
                    )}
                    {isUrgent && !isOverdue && (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-600">
                        即將到期
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">申請人</p>
                      <p className="font-medium text-gray-900">{approval.requesterName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">申請類型</p>
                      <p className="font-medium text-gray-900">{getRequestTypeText(approval.requestType)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">金額</p>
                      <p className="font-medium text-gray-900">{formatCurrency(approval.amount)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">當前核准人</p>
                      <p className="font-medium text-gray-900">{approval.currentApproverName}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>申請時間: {formatDateTime(approval.requestDate)}</span>
                    <span>截止時間: {formatDateTime(approval.dueDate)}</span>
                    <span className={isOverdue ? 'text-red-600' : isUrgent ? 'text-orange-600' : 'text-gray-500'}>
                      {isOverdue ? `逾期 ${Math.abs(daysRemaining)} 天` : 
                       daysRemaining === 0 ? '今天到期' :
                       `剩餘 ${daysRemaining} 天`}
                    </span>
                  </div>
                  
                  <p className="text-gray-700 mt-2">{approval.description}</p>
                </div>
                
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => {
                      setSelectedApproval(approval);
                      setActiveView('approval-detail');
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                  <button className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors">
                    核准
                  </button>
                  <button className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors">
                    拒絕
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderApprovalDetail = () => {
    if (!selectedApproval) return null;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setActiveView('pending')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{selectedApproval.requestTitle}</h2>
              <p className="text-gray-600">{selectedApproval.id}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
              <CheckCircle className="w-4 h-4 inline mr-2" />
              核准
            </button>
            <button className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors">
              <XCircle className="w-4 h-4 inline mr-2" />
              拒絕
            </button>
          </div>
        </div>

        {/* Request Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">申請金額</h3>
            <p className="text-3xl font-bold text-blue-600">{formatCurrency(selectedApproval.amount)}</p>
            <p className="text-sm text-gray-500 mt-2">幣別: {selectedApproval.currency}</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">當前層級</h3>
            <p className="text-3xl font-bold text-orange-600">第 {selectedApproval.currentLevel} 層</p>
            <p className="text-sm text-gray-500 mt-2">核准人: {selectedApproval.currentApproverName}</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">優先級</h3>
            <span className={`inline-flex px-3 py-1 text-lg font-semibold rounded-full ${getPriorityColor(selectedApproval.priority)}`}>
              {getPriorityText(selectedApproval.priority)}
            </span>
            <p className="text-sm text-gray-500 mt-2">類型: {getRequestTypeText(selectedApproval.requestType)}</p>
          </div>
        </div>

        {/* Request Details */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">申請詳情</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">申請人</p>
                <p className="font-medium text-gray-900">{selectedApproval.requesterName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">申請時間</p>
                <p className="font-medium text-gray-900">{formatDateTime(selectedApproval.requestDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">截止時間</p>
                <p className="font-medium text-gray-900">{formatDateTime(selectedApproval.dueDate)}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">相關單據</p>
                <p className="font-medium text-gray-900">{selectedApproval.requestId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">申請說明</p>
                <p className="font-medium text-gray-900">{selectedApproval.description}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Approval History */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">核准歷程</h3>
          <div className="space-y-4">
            {selectedApproval.approvalHistory.map((history, index) => (
              <div key={index} className="flex items-start space-x-4 p-4 border rounded-lg">
                <div className="flex-shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    history.status === 'approved' ? 'bg-green-100' :
                    history.status === 'pending' ? 'bg-yellow-100' :
                    history.status === 'rejected' ? 'bg-red-100' : 'bg-gray-100'
                  }`}>
                    {history.status === 'approved' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : history.status === 'pending' ? (
                      <Clock className="w-4 h-4 text-yellow-600" />
                    ) : history.status === 'rejected' ? (
                      <XCircle className="w-4 h-4 text-red-600" />
                    ) : (
                      <User className="w-4 h-4 text-gray-600" />
                    )}
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <p className="font-medium text-gray-900">第 {history.level} 層 - {history.approverName}</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(history.status)}`}>
                      {getStatusText(history.status)}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-500">
                    <p>指派時間: {formatDateTime(history.assignedDate)}</p>
                    {history.approvedDate && (
                      <p>核准時間: {formatDateTime(history.approvedDate)}</p>
                    )}
                    {history.comments && (
                      <p className="mt-2 text-gray-700">備註: {history.comments}</p>
                    )}
                  </div>
                </div>
                
                {index < selectedApproval.approvalHistory.length - 1 && (
                  <div className="flex-shrink-0">
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Attachments */}
        {selectedApproval.attachments && selectedApproval.attachments.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">相關附件</h3>
            <div className="space-y-2">
              {selectedApproval.attachments.map((attachment, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{attachment.name}</p>
                  </div>
                  <button className="text-blue-600 hover:text-blue-800">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Panel */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">核准操作</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">核准意見</label>
              <textarea
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="請輸入核准或拒絕的理由..."
              />
            </div>
            
            <div className="flex items-center space-x-3">
              <button className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors">
                <CheckCircle className="w-4 h-4 inline mr-2" />
                核准申請
              </button>
              <button className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors">
                <XCircle className="w-4 h-4 inline mr-2" />
                拒絕申請
              </button>
              <button className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 transition-colors">
                <MessageSquare className="w-4 h-4 inline mr-2" />
                要求補件
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'pending':
        return renderPendingApprovals();
      case 'approval-detail':
        return renderApprovalDetail();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="p-6">
      {renderWorkflowBanner()}
      
      {/* Navigation Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveView('dashboard')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeView === 'dashboard'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            儀表板
          </button>
          <button
            onClick={() => setActiveView('pending')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeView === 'pending'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            待核准項目
          </button>
        </nav>
      </div>

      {renderContent()}
    </div>
  );
};

export default ApprovalWorkflow; 